import AIPrompt from 'models/AIPrompt';

export type ProvenanceEntity = { id: string; name: string; type?: string };

export type ProvenanceUser = { id: string; fullName: string };

export type Provenance = {
  type?: 'user_upload' | 'integration' | 'generated' | string;
  facility?: string;
  reason?: string;
  source?: string;
};

export type Source = {
  attachmentId: string;
  filename: string;
  displayId: number;
  createdAt?: string;
  provenance?: Provenance;
  createdByEntity?: ProvenanceEntity | null;
  createdByUser?: ProvenanceUser | null;
};

export type SectionSource = { attachmentId: string; chunkIds: string[] };

export type CitedChunk = { id: string; attachmentId: string; page: number };

export type Section = {
  title: string;
  attachmentIds: string[];
  content: ContentType[];
  sources?: SectionSource[];
};

export type ContentType = { text: string; attachmentIds?: string[]; children?: ContentType[] };

export enum AIArtifactType {
  SUMMARY = 'summary',
  COMPARISON = 'comparison',
}

export interface AIArtifactOptions {
  id: string;
  type: string;
  data: unknown;
  createdAt: Date | null;
  createdBy: {
    id: string;
    fullName: string;
    group?: string;
  };
  aiPrompt: AIPrompt;
}

/**
 * @class AIArtifact
 * @classdesc Represents an AI-generated artifact in the system
 * @property {string} id - The artifact's id
 * @property {string} type - The artifact's type
 * @property {unknown} data - The artifact's data
 * @property {string | null} createdAt - The artifact's creation date
 * @property {User | null} createdByUser - The user who created the artifact
 * @property {unknown} aiPrompt - The artifact's AI prompt
 * @param {Partial<AIArtifactOptions>} [options={}]
 */
export default class AIArtifact {
  id: string;
  type: string;
  data: unknown;
  createdAt: Date | null;
  createdBy: {
    id: string;
    fullName: string;
    group?: string;
  } | null;
  aiPrompt: AIPrompt | null;

  constructor(options?: Partial<AIArtifactOptions>) {
    const opts = { ...options };
    this.id = opts.id || '';
    this.type = opts.type || '';
    this.data = opts.data || {};
    this.createdAt = opts.createdAt || null;
    this.createdBy = opts.createdBy || null;
    this.aiPrompt = opts.aiPrompt ? new AIPrompt(opts.aiPrompt) : null;
  }

  serialize() {
    return {
      id: this.id,
      type: this.type,
      data: this.data,
      createdAt: this.createdAt,
      createdBy: this.createdBy,
      aiPrompt: this.aiPrompt,
    };
  }
}
